import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MdbModalRef } from 'mdb-angular-ui-kit/modal';
import { finalize } from 'rxjs/operators';
import { MESSAGE_CONSTANTS } from 'src/app/constants/message.constant';
import { NIGHTWEB_APP_CONSTANT } from 'src/app/constants/nightweb.constant';
import { OrganizationService, UserService, AdminService, ToasterService } from 'src/app/services';
import { NightwebUtil } from 'src/app/utils';

@Component({
  selector: 'app-invite-end-user-modal',
  templateUrl: './invite-end-user-modal.component.html',
  standalone: false
})
export class InviteEndUserModalComponent implements OnInit {

  inviteForm!: FormGroup;

  loading = false;
  submitted = false;

  organizationsList: any[] = [];
  advocateList: any[] = [];
  role: string = '';
  roles = NIGHTWEB_APP_CONSTANT.roles;

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private toasterService: ToasterService,
    private organizationService: OrganizationService,
    private adminService: AdminService,
    private nightwebUtil: NightwebUtil,
    public modalRef: MdbModalRef<InviteEndUserModalComponent>
  ) { 
    this.role = this.nightwebUtil.getRole();
    this.setInviteForm();
  }

  ngOnInit(): void {
    this.getAllOrganizations();
  }

  setInviteForm() {
    this.inviteForm = this.formBuilder.group({
      id: [],
      demographic: this.formBuilder.group({
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        email: ['', [Validators.required, Validators.pattern(NIGHTWEB_APP_CONSTANT.emailPattern)]],
      }),
      organizationId: ['', Validators.required],
      advocateId: ['', Validators.required],
    });
  }

  get demographic(): FormGroup {
    return this.inviteForm.get('demographic') as FormGroup;
  }

  get isOrgReadonly(): boolean {
    return this.role === this.roles.ADVOCATE || this.role === this.roles.CLIENT_ADMIN;
  }
  get isAdvocateReadonly(): boolean {
    return this.role === this.roles.ADVOCATE;
  }

  sendInvite() {
    this.submitted = true;
    if (this.inviteForm.invalid) {
      return;
    }
    this.loading = true;
    this.userService.inviteEndUser(this.inviteForm.value)
    .pipe(finalize(() => { this.loading = false; this.submitted = false; }))
    .subscribe({
      next: (res) => {
        //if (res.statusCode == this.status_codes.OK) {
        const formValue = this.inviteForm.getRawValue();
        const advocate = this.advocateList.find(
          (a: any) => a.id === formValue.advocateId
        );
        const org = this.organizationsList.find(
          (o: any) => o.id === formValue.organizationId
        );
        const invitedUser = {
            ...(res?.data ?? {}),
            demographic: {
                ...(res?.data?.demographic ?? {}),
                ...formValue.demographic,
            },
            status: res?.data?.status ?? 'Invited',
            advocateId: advocate?.id ?? '',
            organizationId: org?.id ?? '',
            advocate: {
              name: advocate?.displayName ?? '',
              organizationName: org?.displayName || org?.businessName || ''
            },
          };
        this.modalRef.close(invitedUser);
        this.toasterService.show('Success', MESSAGE_CONSTANTS.INVITE_END_USER_SENT);
        //}
      },
    });
  }

  close() {
    this.modalRef.close();
  }

  getAllOrganizations() {
    this.organizationService.getAllOrganizations()
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (response) => {
          this.organizationsList = response.data.rows;
          this.applyRoleDefaults();
          if (this.organizationsList.length === 1) {
            this.inviteForm.patchValue({
              organizationId: this.organizationsList[0]?.id
            });
            this.getAdvocates(this.inviteForm.get('organizationId')?.value)
          }
        },
        error: (e) => { }
      })
  }

  applyRoleDefaults() {
    const loggedInUser = this.nightwebUtil.decodeToken(
      this.nightwebUtil.getLoggedInUser()
    );
    const loginOrgId = loggedInUser?.organization?.id;
    if (this.role === this.roles.ADVOCATE && loginOrgId) {
      this.inviteForm.patchValue({
        organizationId: loginOrgId,
        advocateId: loggedInUser.id,
      });
      this.getAdvocates(loginOrgId, loggedInUser.id);
      return;
    }
    if (this.role === this.roles.CLIENT_ADMIN && loginOrgId) {
      this.inviteForm.patchValue({ organizationId: loginOrgId });
      this.getAdvocates(loginOrgId);
      return;
    }
    // Super admin / others: auto-select when only one org is returned
    if (this.organizationsList.length === 1) {
      this.inviteForm.patchValue({
        organizationId: this.organizationsList[0]?.id
      });
      this.getAdvocates(this.inviteForm.get('organizationId')?.value);
    }
  }

  getAdvocates(organizationId?: string, forceAdvocateId?: string) {
    this.adminService.getAdvocates({ organizationId, fromAdd: true })
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (response) => {
          this.advocateList = response.data.rows.map((admin: any) => {
            admin.displayName = this.nightwebUtil.getDisplayName(admin?.demographic)
            return admin;
          });
          if (forceAdvocateId) {
            this.inviteForm.patchValue({ advocateId: forceAdvocateId });
            return;
          }
          if (this.advocateList.length === 1) {
            this.inviteForm.patchValue({
              advocateId: this.advocateList[0]?.id
            });
            return;
          };
          this.inviteForm.controls['advocateId'].setValue(null);
        },
        error: (e) => { }
      })
  }

  receiveSelectedValue(event: any) {
    this.inviteForm.patchValue({
      [event.controlName]: event.value
    });
    if (event.controlName === 'organizationId') {
      this.getAdvocates(event.value);
    }
  }
}